(() => {
  const selector = "[data-mindelor-compare]";
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
  const precisePointer = window.matchMedia("(hover: hover) and (pointer: fine)");

  function reveal(root) {
    root.classList.add("is-visible");
  }

  function enablePointerGlow(root) {
    if (reducedMotion.matches || !precisePointer.matches) return;

    let frame = 0;
    let pointerX = 0;
    let pointerY = 0;

    const paint = () => {
      const bounds = root.getBoundingClientRect();

      if (!bounds.width || !bounds.height) return;

      const x = Math.min(100, Math.max(0, ((pointerX - bounds.left) / bounds.width) * 100));
      const y = Math.min(100, Math.max(0, ((pointerY - bounds.top) / bounds.height) * 100));

      root.style.setProperty("--mindelor-compare-pointer-x", `${x.toFixed(2)}%`);
      root.style.setProperty("--mindelor-compare-pointer-y", `${y.toFixed(2)}%`);
      frame = 0;
    };

    root.addEventListener("pointermove", (event) => {
      pointerX = event.clientX;
      pointerY = event.clientY;

      if (!frame) frame = window.requestAnimationFrame(paint);
    });

    root.addEventListener("pointerleave", () => {
      if (frame) window.cancelAnimationFrame(frame);

      frame = 0;
      root.style.setProperty("--mindelor-compare-pointer-x", "50%");
      root.style.setProperty("--mindelor-compare-pointer-y", "30%");
    });
  }

  const motionStates = new WeakMap();
  function enableComparisonMotion(root) {
    if (motionStates.has(root)) return;
    const controller = new AbortController();
    const { signal } = controller;
    const selectors = [
      '.mindelor-compare__standard', '.mindelor-compare__brands',
      '.mindelor-compare__strengths', '.mindelor-compare__column',
      '.mindelor-compare__choice', '.mindelor-compare__standard-card',
      '.mindelor-compare__brand-card', '.mindelor-compare__choice-card'
    ];
    const surfaces = [...root.querySelectorAll(selectors.join(','))];
    const visibility = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(({target, isIntersecting}) => {
        target.dataset.ambientVisible = String(isIntersecting);
      });
    }) : null;
    surfaces.forEach((surface, index) => {
      surface.dataset.ambientSurface = '';
      surface.dataset.ambientSize = surface.className.includes('-card') ? 'small' : 'large';
      surface.style.setProperty('--ambient-delay', `${-(index * 3 % 23)}s`);
      const decoration = document.createElement('div');
      decoration.className = 'compare-ambient';
      decoration.setAttribute('aria-hidden', 'true');
      // Preserve the strength grid's existing nth-child and last-child border rules.
      if (!surface.classList.contains('mindelor-compare__strengths')) surface.prepend(decoration);
      if (visibility) visibility.observe(surface);
      else surface.dataset.ambientVisible = 'true';
    });
    const details = [
      ['.mindelor-compare__versus', 'versus'],
      ['.mindelor-compare__column--pro .mindelor-compare__icon', 'check'],
      ['.mindelor-compare__column--con .mindelor-compare__icon', 'dot'],
      ['.mindelor-compare__column-header, .mindelor-compare__list li:not(:last-child)', 'line']
    ];
    details.forEach(([selector, type]) => {
      root.querySelectorAll(selector).forEach((element, index) => {
        element.dataset.microMotion = type;
        if (type === 'dot' && element.textContent.trim() === '•') {
          const mark = document.createElement('span');
          mark.className = 'compare-dot-mark';
          mark.textContent = element.textContent;
          element.replaceChildren(mark);
        }
        element.style.setProperty('--micro-delay', `${-(index * 0.65)}s`);
        if (visibility) visibility.observe(element);
        else element.dataset.ambientVisible = 'true';
      });
    });

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'mindelor-compare__motion-toggle';
    let manuallyPaused = false;
    const sync = () => {
      const paused = manuallyPaused || reducedMotion.matches;
      root.dataset.motionPaused = String(paused);
      root.dataset.pageHidden = String(document.hidden);
      button.textContent = reducedMotion.matches ? 'Beweging verminderd' : paused ? 'Beweging hervatten' : 'Beweging pauzeren';
      button.setAttribute('aria-pressed', String(paused));
      button.disabled = reducedMotion.matches;
    };
    button.addEventListener('click', () => { manuallyPaused = !manuallyPaused; sync(); }, { signal });
    reducedMotion.addEventListener('change', sync, { signal });
    document.addEventListener('visibilitychange', sync, { signal });
    (root.querySelector('.mindelor-compare__inner') || root).append(button);
    sync();
    motionStates.set(root, () => { controller.abort(); visibility?.disconnect(); });
  }

  function init(root) {
    if (!(root instanceof HTMLElement) || root.dataset.mindelorCompareReady === "true") return;

    root.dataset.mindelorCompareReady = "true";
    enableComparisonMotion(root);

    // The shared public-page observer handles the copy block by block. Keep the
    // comparison cards and logos stationary instead of revealing the section twice.
    if (root.closest("[data-mindelor-text-scroll-root]")) {
      enablePointerGlow(root);
      return;
    }

    if (reducedMotion.matches) {
      reveal(root);
      return;
    }

    root.classList.add("mindelor-compare--motion-ready");
    enablePointerGlow(root);

    if (!("IntersectionObserver" in window)) {
      reveal(root);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        reveal(root);
        observer.disconnect();
      },
      {
        rootMargin: "0px 0px -12%",
        threshold: 0.12
      }
    );

    observer.observe(root);
  }

  function initAll(scope = document) {
    if (scope instanceof Element && scope.matches(selector)) init(scope);
    scope.querySelectorAll(selector).forEach(init);
  }

  document.addEventListener('shopify:section:unload', event => {
    const roots = [...event.target.querySelectorAll(selector)];
    if (event.target.matches?.(selector)) roots.push(event.target);
    roots.forEach(root => { motionStates.get(root)?.(); motionStates.delete(root); });
  });

  initAll();

  document.addEventListener("shopify:section:load", (event) => {
    initAll(event.target);
  });
})();
